import { App } from '@capacitor/app';
import { registerPlugin } from '@capacitor/core';
import { isIOS } from './Platform';

/**
 * iOS-only — keeps audio alive while the app is backgrounded.
 *
 * Background: WKWebView's <video> audio is suspended ~15s after the app
 * loses focus (WebKit bug #203293, confirmed by the Capacitor team in
 * ionic-team/capacitor#3446). UIBackgroundModes=audio + AVAudioSession
 * .playback is necessary but not sufficient — the constraint is the
 * WebContent process assertion expiring, not audio routing.
 *
 * Workaround: when the app backgrounds, hand the playing source URL +
 * currentTime to a native AVPlayer (the BackgroundAudio Capacitor
 * plugin). AVPlayer in the main app process plays unrestricted under
 * UIBackgroundModes=audio. On resume, we read the elapsed time back,
 * seek the WebView <video>, and tear AVPlayer down.
 *
 * No-op on Android (foreground service + PiP already handle that path)
 * and on plain web.
 */
const BackgroundAudio = registerPlugin('BackgroundAudio');

let attached = false;
let appStateListener = null;
let inHandoff = false;
let getStateFn = null;

function getActiveVideoEl() {
    return document.querySelector('#videoPlayer video, .videoPlayer video');
}

function getCurrentVideoSrc(video) {
    if (!video) return null;
    if (video.currentSrc) return video.currentSrc;
    if (video.src) return video.src;
    const source = video.querySelector('source');
    return source ? source.src : null;
}

async function handleAppPaused() {
    if (!isIOS()) return;
    if (inHandoff) return;
    const video = getActiveVideoEl();
    if (!video || video.paused) return;

    const url = getCurrentVideoSrc(video);
    if (!url) return;

    const startTime = Number.isFinite(video.currentTime)
        ? video.currentTime
        : 0;
    const meta = (typeof getStateFn === 'function' ? getStateFn() : {}) || {};

    try {
        await BackgroundAudio.play({
            url,
            startTime,
            title: meta.title,
            artwork: meta.artwork
        });
        inHandoff = true;
        // Pause the WebView video silently — its audio is about to be
        // killed by WebKit anyway, and leaving it "playing" while muted
        // can confuse the player's own state machine on resume.
        try {
            video.pause();
        } catch (_) {
            /* non-fatal */
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[BackgroundAudio] handoff to native failed', err);
    }
}

async function handleAppResumed() {
    if (!inHandoff) return;
    let resumedAt = 0;
    try {
        const result = await BackgroundAudio.stop();
        resumedAt = (result && result.currentTime) || 0;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[BackgroundAudio] resume handoff failed', err);
    } finally {
        inHandoff = false;
    }

    const video = getActiveVideoEl();
    if (video) {
        try {
            if (resumedAt > 0) video.currentTime = resumedAt;
            await video.play();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[BackgroundAudio] resume video play failed', err);
        }
    }
}

export async function attach({ getState } = {}) {
    if (!isIOS()) return;
    if (attached) return;
    getStateFn = getState || null;
    appStateListener = await App.addListener(
        'appStateChange',
        ({ isActive }) => {
            if (!isActive) handleAppPaused();
            else handleAppResumed();
        }
    );
    attached = true;
}

export async function detach() {
    if (!attached) return;
    if (appStateListener) {
        try {
            await appStateListener.remove();
        } catch (_) {
            /* non-fatal */
        }
        appStateListener = null;
    }
    if (inHandoff) {
        try {
            await BackgroundAudio.stop();
        } catch (_) {
            /* non-fatal */
        }
        inHandoff = false;
    }
    getStateFn = null;
    attached = false;
}

export default { attach, detach };
